import React from 'react';
import { useDispatch } from 'react-redux';

import { useAppContext } from 'src/components/app-context';
import { useProductContext } from '../../../product-context';

import { noop } from 'src/components/base';

import { ProductFilters } from 'src/areas/product/components/product-filters';
import CampaignBadge from 'src/components/campaign-badge';
import ErrorBoundary from 'src/components/error-boundary';
import Rating from 'src/components/rating';
import StarIconsComponent from 'src/components/rating/star-icons';
import SentimentBadge from 'src/components/sentiment-badge';
import { headerClickEvent } from '../../../../actions/product';
import { PriceHistorySummary } from '../../../price-history-summary';
import { ProductMedia } from '../../../product-media';
import { LinkToPriceHistory } from '../link-to-price-history';
import { LinkToReviews } from '../LinkToReviews';
import { RankBadge } from './rank-badge';
import { OurChoice } from '../our-choice';
import { ActionButtons } from '../action-buttons';

import type { ProductHeaderProps } from '../../types';

import RatingHelper from 'src/components/rating/rating-helper';
import {
  StyledBadges,
  StyledContent,
  StyledDesktopHeader,
  StyledDesktopTitle,
  StyledDisplayProperties,
  StyledMedia,
  StyledMore,
  StyledProductSummary,
  StyledRatingWrapper,
  StyledReviewBadges,
  StyledStoreLogo,
  StyledTabletTitle,
  StyledTitle,
  StyledWriteReview,
} from './styles';
import { usePriceHistorySummaryQuery } from './queries/use-price-history-summary-query';

export const ProductHeaderDesktop: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<ProductHeaderProps>>
> = props => {
  const { className, currentCampaign } = props;
  const { product } = useProductContext();
  const { t } = useAppContext();
  const priceHistorySummary = usePriceHistorySummaryQuery({
    id: product.id!,
    statisticsTime: '1970-01-02',
  });

  const dispatch = useDispatch();

  const headerStyle = { minWidth: '100%' };

  const getDisplayProperties = (nodes: any) =>
    nodes.map((node: any) => node.prettyVerbose).filter((item: any) => item);

  const displayProperties =
    product && product.coreProperties
      ? getDisplayProperties(product.coreProperties.nodes)
      : [];

  const handlePriceHistoryClick = () =>
    dispatch(headerClickEvent('price-history'));

  const renderProductSummary = () => {
    product.statistics = priceHistorySummary.data?.product?.statistics;
    const { isLoading } = priceHistorySummary;
    const hasPriceHistoryData =
      product.statistics &&
      product.statistics.nodes &&
      product.statistics.nodes.length > 0 &&
      product.statistics.pageInfo &&
      product.statistics.pageInfo.lowestPrice;
    const hasPriceHistory = Boolean(
      product &&
        product.statistics &&
        product.statistics.nodes &&
        product.statistics.nodes.length > 1
    );

    return (
      <StyledProductSummary>
        {(isLoading || hasPriceHistoryData) && (
          <LinkToPriceHistory onClick={handlePriceHistoryClick}>
            {(isLoading || hasPriceHistory) && (
              <PriceHistorySummary isLoading={isLoading} />
            )}
          </LinkToPriceHistory>
        )}
        <ActionButtons isDesktop />
      </StyledProductSummary>
    );
  };

  const hasNoReviews =
    product.aggregatedRatingSummary?.summary.rating.count === 0 ||
    (!product.aggregatedRatingSummary && product.userReviewSummary.count === 0);

  return (
    <StyledDesktopHeader className={className} data-test="DesktopProductHeader">
      <StyledTabletTitle style={headerStyle} data-test="ProductTitle">
        <StyledTitle $outer>{product.name}</StyledTitle>
      </StyledTabletTitle>
      <StyledMedia>
        <ErrorBoundary onError={noop}>
          <StyledBadges>
            {currentCampaign ? (
              <CampaignBadge
                colors={currentCampaign.media.colors}
                size="large"
              />
            ) : null}
            {product.category &&
              product.popularity &&
              Boolean(product.popularity.inCategory) && (
                <RankBadge
                  category={{
                    pathName: product.category.pathName,
                    name: product.category.name || '',
                  }}
                  rank={product.popularity.inCategory}
                  isExperiment
                />
              )}
          </StyledBadges>
        </ErrorBoundary>
        <ErrorBoundary onError={noop}>
          {product.media && (
            <ProductMedia
              id={product.id}
              name={product.name}
              category={product.category}
            />
          )}
        </ErrorBoundary>
      </StyledMedia>

      <StyledContent>
        <StyledDesktopTitle data-test="ProductTitle">
          <StyledTitle $inner>{product.name}</StyledTitle>
        </StyledDesktopTitle>
        <ErrorBoundary onError={noop}>
          <>
            <StyledMore>
              <StyledRatingWrapper>
                <LinkToReviews>
                  <Rating
                    hide={hasNoReviews}
                    rate={
                      product.aggregatedRatingSummary?.summary?.rating?.score ||
                      product.userReviewSummary.rating
                    }
                    count={
                      product.aggregatedRatingSummary?.summary?.rating?.count ||
                      product.userReviewSummary.count
                    }
                    size="large"
                    showRate
                    showRateAs="number"
                    showCounterAs="number"
                    styleCounterAsLink
                  />

                  {hasNoReviews && (
                    <StyledWriteReview>
                      <StarIconsComponent
                        stars={[1]}
                        rating={RatingHelper.convert(0)}
                        halfStar={false}
                      />
                      {t('reviews', 'button.write-review')}
                    </StyledWriteReview>
                  )}
                </LinkToReviews>
              </StyledRatingWrapper>
              {Boolean(displayProperties.length) && (
                <StyledDisplayProperties
                  toneDown
                  maxrows={3}
                  properties={displayProperties
                    // Cut off at max three core properties
                    .slice(0, 3)}
                />
              )}

              {product.reviewBadges && product.reviewBadges.length > 0 ? (
                <StyledReviewBadges>
                  {product.reviewBadges.slice(0, 3).map((badge: any) => (
                    <SentimentBadge
                      badge={badge}
                      key={badge.id}
                      showNrOfReviewsAs="text"
                    />
                  ))}
                </StyledReviewBadges>
              ) : (
                ''
              )}
            </StyledMore>

            <ProductFilters />
            <OurChoice campaign={currentCampaign} />
          </>
        </ErrorBoundary>
      </StyledContent>

      {renderProductSummary()}

      {product.brand && product.brand.featured && (
        <StyledStoreLogo
          title={t('product', 'store-info.more-about-store', {
            store_name: product.brand.name,
          })}
          to={product.brand.pathName}
        >
          {product.brand.featured && product.brand.logo ? (
            <img src={product.brand.logo} alt={product.brand.name} />
          ) : (
            product.brand.name
          )}
        </StyledStoreLogo>
      )}
    </StyledDesktopHeader>
  );
};
